/**
 * Tesoreria › Carte di credito (10/10/2026): the cards of K-Rev and their
 * spending, loaded from the files of the portal of Intesa Sanpaolo
 * («situazioneCarte» and «listaMovimenti»). Of a card only the last four
 * digits are kept (specification M8). The spending serves to close the
 * invoices paid by card, to check the monthly debit of the bank and to keep
 * the costs under control.
 */
import { z } from 'zod';
import { cellCents, cellDate, cellText } from './bank-statements.js';
import { addDays } from './dates.js';
import { fromCents, parseDecimal, toCents } from './money.js';
import { optionalText, requiredText } from './validation.js';
export const CARD_KINDS = ['credito', 'debito'];
export const CARD_KIND_LABELS = {
    credito: 'Credito',
    debito: 'Debito',
};
export const CARD_STATES = ['attiva', 'da consegnare', 'da restituire', 'chiusa'];
export const CARD_STATE_LABELS = {
    attiva: 'Attiva',
    'da consegnare': 'Da consegnare',
    'da restituire': 'Da restituire',
    chiusa: 'Chiusa',
};
/** The table of Tabelle with the categories of the shops (MCC codes). */
export const CARD_CATEGORY_TABLE = 'categorie-carte';
const money = z
    .string()
    .trim()
    .regex(/^\d{1,12}([.,]\d{1,2})?$/, { message: 'Importo non valido' })
    .transform((value) => fromCents(toCents(value.replace(',', '.'))));
export const cardSchema = z.object({
    last4: z
        .string()
        .trim()
        .regex(/^\d{4}$/, { message: 'Le ultime 4 cifre della carta' }),
    holder: requiredText(120),
    kind: z.enum(CARD_KINDS),
    spendingLimit: money.nullable(),
    state: z.enum(CARD_STATES),
    /** The account the card is charged to: code of the table «Conti». */
    accountCode: optionalText(30),
    notes: optionalText(500),
});
/** «4111********1234», «4111000000001234» → «1234». */
export function cardLast4(value) {
    const digits = cellText(value).replace(/\D/g, '');
    return digits.length >= 4 ? digits.slice(-4) : null;
}
/**
 * A file of the portal of Intesa, recognised from its headings (the first
 * rows are empty): the list of the cards or the list of the spending.
 */
export function readCardFile(cells) {
    const headingAt = cells.findIndex((row) => row.some((cell) => /^(Numero carta|Data)$/i.test(cellText(cell))));
    if (headingAt < 0) {
        return {
            kind: 'movimenti',
            cards: [],
            movements: [],
            problems: ['Il file non è un export delle carte (manca la riga «Numero carta» o «Data»)'],
        };
    }
    const headings = cells[headingAt].map((cell) => cellText(cell).toLowerCase());
    const column = (...names) => headings.findIndex((name) => names.includes(name));
    const rows = cells.slice(headingAt + 1);
    if (column('numero carta') >= 0)
        return readCardList(rows, headingAt, column);
    return readCardMovements(rows, headingAt, column);
}
function readCardList(rows, headingAt, column) {
    const at = {
        number: column('numero carta'),
        holder: column('intestatario'),
        category: column('categoria'),
        state: column('stato'),
        limit: column('limite di spesa'),
    };
    const problems = [];
    const cards = [];
    rows.forEach((row, index) => {
        if (!row.some((cell) => cellText(cell)))
            return;
        const where = `riga ${headingAt + index + 2}`;
        const last4 = cardLast4(row[at.number]);
        if (!last4)
            return problems.push(`${where}: numero della carta non leggibile`);
        const limit = cellCents(row[at.limit]);
        cards.push({
            last4,
            holder: cellText(row[at.holder]).slice(0, 120) || 'Da completare',
            kind: /DEBIT/i.test(cellText(row[at.category])) ? 'debito' : 'credito',
            // A debit card has no limit: the bank writes 999.999.999.
            spendingLimit: limit !== null && limit > 0 && limit < 10_000_000_000 ? fromCents(limit) : null,
            bankState: cellText(row[at.state]).toUpperCase(),
        });
    });
    return { kind: 'carte', cards, movements: [], problems };
}
function readCardMovements(rows, headingAt, column) {
    const at = {
        date: column('data'),
        description: column('descrizione'),
        mcc: column('cat. merc.', 'categoria merceologica'),
        original: column('importo originario'),
        currency: column('divisa'),
        rate: column('cambio'),
        amount: column('importo'),
        card: column('carta'),
    };
    const missing = Object.entries(at)
        .filter(([key, position]) => position < 0 && key !== 'mcc' && key !== 'rate')
        .map(([key]) => key);
    if (missing.length) {
        return {
            kind: 'movimenti',
            cards: [],
            movements: [],
            problems: ['Mancano le colonne Data, Descrizione, Importo o Carta'],
        };
    }
    const problems = [];
    const movements = [];
    const seen = new Map();
    rows.forEach((row, index) => {
        if (!row.some((cell) => cellText(cell)))
            return;
        const where = `riga ${headingAt + index + 2}`;
        const date = cellDate(row[at.date]);
        const amount = cellCents(row[at.amount]);
        const last4 = cardLast4(row[at.card]);
        if (!date)
            return problems.push(`${where}: data non leggibile`);
        if (amount === null)
            return problems.push(`${where}: importo non leggibile`);
        if (!last4)
            return problems.push(`${where}: carta non leggibile`);
        const description = cellText(row[at.description]).slice(0, 200) || 'Spesa senza descrizione';
        const original = cellCents(row[at.original]) ?? amount;
        const currency = cellText(row[at.currency]).toUpperCase().slice(0, 3) || 'EUR';
        const rate = at.rate >= 0 ? parseDecimal(cellText(row[at.rate])) : null;
        const mcc = at.mcc >= 0 ? cellText(row[at.mcc]).replace(/\D/g, '').slice(0, 4) : '';
        const key = `${last4}:${date}:${amount}:${description.toLowerCase()}`;
        const occurrence = (seen.get(key) ?? 0) + 1;
        seen.set(key, occurrence);
        movements.push({
            last4,
            date,
            description,
            mcc: mcc || null,
            originalAmount: fromCents(original),
            currency,
            exchangeRate: currency !== 'EUR' && rate !== null && rate > 0 ? rate.toFixed(6) : null,
            amount: fromCents(amount),
            bankRef: (occurrence > 1 ? `${key}#${occurrence}` : key).slice(0, 300),
        });
    });
    return { kind: 'movimenti', cards: [], movements, problems };
}
/** The state of a card from the one written by the bank; null when the bank says nothing new. */
export function cardStateFromBank(bankState) {
    if (/CONSEGNA/.test(bankState))
        return 'da consegnare';
    if (/(ESTINT|CHIUS|REVOC|BLOCC|SCADUT)/.test(bankState))
        return 'chiusa';
    return null;
}
/**
 * The shop of a spending without the references that change every time:
 * «GOOGLE*CLOUD 7PWJNZ», «Google CLOUD SWBFVF» → «GOOGLE CLOUD»;
 * «MICROSOFT#G072677657» → «MICROSOFT». It names the supplier in the summary.
 */
export function merchantName(description) {
    const words = description
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[*#_]+|-(?=\S*\d)/g, ' ')
        .split(/\s+/)
        .filter(Boolean);
    const kept = words.filter((word, index) => 
    // Words with digits are references: an order, a branch, a booking;
    // so are the codes of letters without vowels after the name («SWBFVF»).
    !/\d/.test(word) && !(index > 0 && /^[B-DF-HJ-NP-TV-Z]{5,}$/i.test(word)));
    const name = kept
        .join(' ')
        .replace(/[^A-Za-z&.' -]+/g, ' ')
        .replace(/\s+/g, ' ')
        .replace(/[\s.-]+$/g, '')
        .trim();
    if (name)
        return name.toUpperCase();
    // Name and reference stuck together: «MICROSOFTG139447559» → «MICROSOFT».
    const stuck = /^([A-Za-z]{4,}?)[A-Za-z]?\d+$/.exec(words[0] ?? '');
    return (stuck ? stuck[1] : description.trim()).toUpperCase();
}
/** The key that groups the spending of the same shop. */
export function merchantKey(description) {
    return merchantName(description).toLowerCase();
}
/**
 * The entry of the table of the categories for a code: airlines, car rentals
 * and hotels have a code each (3000–3999), the table groups them.
 */
export function cardCategoryCode(mcc) {
    if (!mcc)
        return null;
    const code = Number(mcc);
    if (code >= 3000 && code <= 3350)
        return 'G300';
    if (code >= 3351 && code <= 3500)
        return 'G335';
    if (code >= 3501 && code <= 3999)
        return 'G350';
    return String(code);
}
/* --- Loading ---------------------------------------------------------------- */
export const loadCardFilesSchema = z.object({
    files: z
        .array(z.object({
        fileName: z.string().trim().min(1).max(200),
        /** The .xls or .xlsx of the bank, in base64. */
        excel: z.string().min(1).max(4_000_000),
    }))
        .min(1)
        .max(30),
    dryRun: z.boolean().default(false),
});
export const cardMovementsQuerySchema = z.object({
    year: z.coerce.number().int().min(2000).max(2100),
    month: z.coerce.number().int().min(1).max(12).optional(),
    cardId: z.uuid().optional(),
    category: z.string().trim().max(10).optional(),
    q: z.string().trim().max(100).optional(),
});
export const SPENDING_GROUPS = ['categoria', 'carta', 'fornitore'];
export const SPENDING_GROUP_LABELS = {
    categoria: 'Categoria',
    carta: 'Carta',
    fornitore: 'Fornitore',
};
export const cardSpendingQuerySchema = z.object({
    year: z.coerce.number().int().min(2000).max(2100),
    by: z.enum(SPENDING_GROUPS).default('categoria'),
});
/**
 * The spending of a year by month and group, largest first, with the same
 * months of the year before: up to the last month with spending, so that the
 * current year is compared with the same part of the previous one.
 */
export function spendingSummary(items, year) {
    const prefix = String(year);
    const before = String(year - 1);
    const lastMonth = items
        .filter((item) => item.date.startsWith(prefix))
        .reduce((last, item) => Math.max(last, Number(item.date.slice(5, 7))), 0);
    const groups = new Map();
    const add = (key, label) => {
        const found = groups.get(key) ?? { label, months: Array(12).fill(0), previous: 0 };
        groups.set(key, found);
        return found;
    };
    for (const item of items) {
        const month = Number(item.date.slice(5, 7));
        const cents = toCents(item.amount);
        if (item.date.startsWith(prefix))
            add(item.key, item.label).months[month - 1] += cents;
        else if (item.date.startsWith(before) && month <= lastMonth) {
            add(item.key, item.label).previous += cents;
        }
    }
    const row = (key, label, months, previous) => ({
        key,
        label,
        months: months.map(fromCents),
        total: fromCents(months.reduce((sum, value) => sum + value, 0)),
        previous: fromCents(previous),
    });
    const rows = [...groups.entries()]
        .filter(([, group]) => group.months.some(Boolean) || group.previous)
        .map(([key, group]) => row(key, group.label, group.months, group.previous))
        .sort((a, b) => toCents(b.total) - toCents(a.total) || a.label.localeCompare(b.label));
    const months = Array(12).fill(0);
    let previous = 0;
    for (const group of groups.values()) {
        group.months.forEach((value, index) => (months[index] += value));
        previous += group.previous;
    }
    return { lastMonth, rows, totals: row('totale', 'Totale', months, previous) };
}
/* --- Monthly debits (1.54) ----------------------------------------------------- */
/**
 * The debit of the credit cards on the bank account: Intesa writes «ADD. E/C
 * CARTA», causale ABI 45 «Pagamento per utilizzo carte di credito».
 */
export function isCardDebit(abiCode, description) {
    if ((abiCode ?? '').split('/')[0].trim() === '45')
        return true;
    return /ADD\.?\s*E\/C\s*CARTA|UTILIZZO CARTE DI CREDITO/i.test(description);
}
/**
 * The month whose spending a debit pays: the bank values it on the 28th of
 * the month after («28/09» → August), and books it a few days later at most.
 */
export function cardDebitMonth(date) {
    return previousMonth(addDays(date, -7).slice(0, 7));
}
const MONTH_NAMES = [
    'gennaio',
    'febbraio',
    'marzo',
    'aprile',
    'maggio',
    'giugno',
    'luglio',
    'agosto',
    'settembre',
    'ottobre',
    'novembre',
    'dicembre',
];
/** «2026-08» → «agosto 2026». */
export function cardMonthLabel(month) {
    return `${MONTH_NAMES[Number(month.slice(5, 7)) - 1] ?? month} ${month.slice(0, 4)}`;
}
/** The day the bank values the debit of the spending of a month: the 28th of the month after. */
export function cardDebitDueDate(month) {
    return `${nextMonth(month)}-28`;
}
export const CARD_DEBIT_STATES = [
    'quadra',
    'passa',
    'sospeso',
    'da-verificare',
    'atteso',
    'non-trovato',
];
/**
 * The monthly debits of the credit cards against their spending (decision of
 * 10/10/2026). The bank debits the spending of a month on the 28th of the
 * next; what it registers late passes to the debit after. So the running sum
 * of «debit − spending» always returns to the same level: the spending before
 * the first file, debited with the first month. Below that level the
 * difference is spending still to debit («in sospeso»), which can only be
 * spending of the last days of the month: more than that, or a debit above
 * the spending, is to check. A missing debit breaks the sum: the months after
 * start again.
 */
export function cardDebits(spending, debits, today) {
    const byMonth = new Map();
    for (const item of spending) {
        const month = item.date.slice(0, 7);
        byMonth.set(month, (byMonth.get(month) ?? 0) + toCents(item.amount));
    }
    const first = [...byMonth.keys()].sort()[0];
    if (!first)
        return { carryOver: '0.00', rows: [] };
    const paid = new Map();
    for (const debit of debits) {
        const month = cardDebitMonth(debit.valueDate ?? debit.date);
        // Before the first file: the spending it pays is not there.
        if (month < first)
            continue;
        paid.set(month, [...(paid.get(month) ?? []), debit]);
    }
    const last = [...byMonth.keys(), ...paid.keys()].sort().at(-1);
    const rows = [];
    for (let month = first; month <= last; month = nextMonth(month)) {
        const own = paid.get(month) ?? [];
        rows.push({
            month,
            spending: byMonth.get(month) ?? 0,
            own,
            debit: own.length ? -own.reduce((sum, item) => sum + toCents(item.amount), 0) : null,
            running: 0,
            pending: 0,
        });
    }
    // The months with their debit, in runs without gaps, each with its level.
    let carryOver = null;
    let run = [];
    const close = () => {
        if (!run.length)
            return;
        const level = runningLevel(run.map((row) => row.running));
        carryOver ??= level;
        for (const row of run)
            row.pending = level - row.running;
        run = [];
    };
    let running = 0;
    for (const row of rows) {
        if (row.debit === null) {
            close();
            running = 0;
            continue;
        }
        running += row.debit - row.spending;
        row.running = running;
        run.push(row);
    }
    close();
    const result = rows.map((row, index) => {
        const dueDate = cardDebitDueDate(row.month);
        const base = {
            month: row.month,
            spending: fromCents(row.spending),
            dueDate,
            debits: row.own.map((item) => ({
                movementId: item.movementId,
                accountCode: item.accountCode,
                date: item.date,
                amount: fromCents(-toCents(item.amount)),
            })),
        };
        if (row.debit === null) {
            // A week after the 28th the debit should be among the movements.
            const state = addDays(dueDate, 7) < today ? 'non-trovato' : 'atteso';
            return {
                ...base,
                debit: null,
                difference: null,
                pending: null,
                state,
                lateSpending: [],
                probable: [],
            };
        }
        const from = addDays(`${nextMonth(row.month)}-01`, -LATE_DAYS);
        const late = row.pending > 0
            ? spending.filter((item) => item.date.startsWith(row.month) && item.date >= from)
            : [];
        const lateTotal = late.reduce((sum, item) => sum + toCents(item.amount), 0);
        // In suspense there can only be spending of the last days, waiting for the next debit.
        let state = 'quadra';
        if (row.pending < 0 || row.pending > lateTotal)
            state = 'da-verificare';
        else if (row.pending > 0)
            state = rows[index + 1]?.debit != null ? 'passa' : 'sospeso';
        return {
            ...base,
            debit: fromCents(row.debit),
            difference: fromCents(row.debit - row.spending),
            pending: fromCents(row.pending),
            state,
            lateSpending: late.map((item) => item.id),
            probable: row.pending > 0 ? summingTo(late, row.pending) : [],
        };
    });
    return { carryOver: fromCents(carryOver ?? 0), rows: result.reverse() };
}
/** The days at the end of a month whose spending the bank may register in the next one. */
const LATE_DAYS = 7;
function nextMonth(month) {
    const year = Number(month.slice(0, 4));
    const number = Number(month.slice(5, 7));
    return number === 12 ? `${year + 1}-01` : `${year}-${String(number + 1).padStart(2, '0')}`;
}
function previousMonth(month) {
    const year = Number(month.slice(0, 4));
    const number = Number(month.slice(5, 7));
    return number === 1 ? `${year - 1}-12` : `${year}-${String(number - 1).padStart(2, '0')}`;
}
/**
 * The level the running sum returns to: the value it takes most often; with
 * no value repeated, the highest (below it there is spending still to debit).
 */
function runningLevel(values) {
    const counts = new Map();
    for (const value of values)
        counts.set(value, (counts.get(value) ?? 0) + 1);
    let best = null;
    let bestCount = 1;
    for (const [value, count] of counts) {
        if (count > bestCount || (count === bestCount && best !== null && value > best)) {
            best = value;
            bestCount = count;
        }
    }
    return best ?? Math.max(0, ...values);
}
/** Up to four spending items that make exactly the amount; none when no such set exists. */
function summingTo(items, cents) {
    const values = items.map((item) => toCents(item.amount));
    const pick = (start, left, size, chosen) => {
        if (size === 0)
            return left === 0 ? chosen : null;
        for (let index = start; index < values.length; index += 1) {
            const found = pick(index + 1, left - values[index], size - 1, [...chosen, index]);
            if (found)
                return found;
        }
        return null;
    };
    for (let size = 1; size <= 4; size += 1) {
        const found = pick(0, cents, size, []);
        if (found)
            return found.map((index) => items[index].id);
    }
    return [];
}
//# sourceMappingURL=credit-cards.js.map